import {
	Button,
	Dialog,
	IconButton,
	LuIcon,
	Separator,
	stopPropagation,
	Text,
	toast,
	useAlertDialog,
} from "@overmap-ai/blocks"
import { type Asset, AssetStatusType, type AssetType, ASSET_STATUS_TYPES } from "@overmap-ai/common"
import { selectAssetTypesByIds } from "@overmap-ai/core"
import * as RadixAccordian from "@radix-ui/react-accordion"
import { memo, useCallback, useMemo } from "react"

import { assetStatusTypeMapping } from "../../mappings/assets"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useShallowEqaulSelector } from "../../store"
import { selectFirstAssetTypeStatusByStatusTypeByAssetType } from "../../store/selectors"
import type { ExtendableDialogProps } from "../Dialogs/typings"
import { AssetStatusTypeDropdownMenu } from "../DropdownMenus/AssetStatusTypeDropdownMenu"
import { BulkAssetControlAssetTypeItem } from "./BulkAssetControlAssetTypeItem"

export interface BulkAssetControlProps extends ExtendableDialogProps {
	selectedAssetsByAssetType: Record<AssetType["uuid"], Asset["uuid"][]>
}

export const BulkAssetControl = memo((props: BulkAssetControlProps) => {
	const {
		children,
		open,
		defaultOpen,
		onOpenChange, // oxlint-disable-line unbound-method
		modal,
		selectedAssetsByAssetType,
		...rest
	} = props
	const openAlertDialog = useAlertDialog()

	const assetTypeIds = useMemo(() => Object.keys(selectedAssetsByAssetType), [selectedAssetsByAssetType])
	const assetTypes = useShallowEqaulSelector(selectAssetTypesByIds(assetTypeIds))
	const firstAssetTypeStatusByStatusTypeByAssetType = useShallowEqaulSelector(
		selectFirstAssetTypeStatusByStatusTypeByAssetType(assetTypeIds),
	)

	const selectedAssetCount = useMemo(
		() => assetTypeIds.reduce((sum, assetTypeId) => sum + selectedAssetsByAssetType[assetTypeId].length, 0),
		[assetTypeIds, selectedAssetsByAssetType],
	)

	const handleBulkAssetStatusTypeChange = useCallback(
		(assetStatusType: AssetStatusType) => {
			const { label } = assetStatusTypeMapping[assetStatusType]
			openAlertDialog({
				title: "Confirm mass status change?",
				description: `You are about to set the status to "${label}" on ${selectedAssetCount} assets. Are you sure?`,
				size: "sm",
				onAction: () => {
					const updates = Object.entries(selectedAssetsByAssetType).flatMap(([assetTypeId, assetIds]) => {
						const status = firstAssetTypeStatusByStatusTypeByAssetType[assetTypeId][assetStatusType]
						if (!status) return []
						return assetIds.map((uuid) => ({ uuid, status: status.uuid }))
					})

					if (updates.length === 0) return

					sdk.assets.bulkUpdate(updates)

					toast.success({
						title: "Status updated",
						description: `Status set on ${updates.length} assets.`,
					})
				},
			})
		},
		[selectedAssetsByAssetType, firstAssetTypeStatusByStatusTypeByAssetType, openAlertDialog, selectedAssetCount],
	)

	return (
		<Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal={modal}>
			{!!children && <Dialog.Trigger asChild>{children}</Dialog.Trigger>}
			<Dialog.Overlay onClick={stopPropagation} />
			<Dialog.Content className="flex flex-col gap-4" onClick={stopPropagation} {...rest}>
				<div className="flex flex-col">
					<div className="flex justify-between gap-2">
						<Dialog.Title>
							<Text size="lg">Asset edit</Text>
						</Dialog.Title>

						<Dialog.Close asChild>
							<IconButton variant="ghost" accentColor="base">
								<LuIcon icon={modelIconMapping.close} />
							</IconButton>
						</Dialog.Close>
					</div>
					<Dialog.Description asChild>
						<Text size="sm" accentColor="base">
							Click on individual assets to edit fields or procedures
						</Text>
					</Dialog.Description>
				</div>
				<Separator size="full" />

				<div className="flex w-full items-center justify-between gap-2 pr-4">
					<Text size="sm" accentColor="base">
						Set all {selectedAssetCount} assets
					</Text>
					<AssetStatusTypeDropdownMenu
						assetStatusTypes={ASSET_STATUS_TYPES}
						onSelectAssetStatusType={handleBulkAssetStatusTypeChange}
						size="sm"
					>
						<Button size="sm" variant="soft" accentColor="base">
							<LuIcon icon={modelIconMapping.status} />
							Status
						</Button>
					</AssetStatusTypeDropdownMenu>
				</div>

				<div className="flex grow flex-col gap-4 overflow-hidden">
					<RadixAccordian.Root
						className="flex min-h-0 grow flex-col gap-2 overflow-y-auto"
						type="single"
						collapsible
						role="list"
					>
						{assetTypes.map((assetType) => (
							<BulkAssetControlAssetTypeItem
								key={assetType.uuid}
								assetType={assetType}
								assetIds={selectedAssetsByAssetType[assetType.uuid]!}
							/>
						))}
					</RadixAccordian.Root>
				</div>
			</Dialog.Content>
		</Dialog.Root>
	)
})

BulkAssetControl.displayName = "BulkAssetControl"
